import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { UserPlus, Trash2, Users, KeyRound, Copy, CheckCircle2, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Partner Portal → Team page (v1.10.28+).
 *
 * Two roles live under a partner:
 *   • ``partner``       → **Partner Admin** — sees commissions, payouts
 *                         and dashboards; can invite/remove/reset staff.
 *   • ``partner_staff`` → **Technician** — everything except money
 *                         (onboarding, tickets, customers, ideas).
 *
 * Backend role values stayed ``partner`` / ``partner_staff`` so no data
 * migration is needed — the human-facing labels are set here only.
 *
 * Technicians are auto-added to the partner's cc_emails on invite so
 * they stay looped in on every partner-facing outbound send. They
 * inherit the tenant's Force-2FA policy on first login.
 *
 * Partner Admins can change any team member's role via the dropdown
 * and issue a fresh temp password via "Reset password". They can't
 * demote themselves and can't leave the partner with zero admins.
 */

const ROLE_LABEL = { partner: "Partner Admin", partner_staff: "Technician" };
const ROLE_OPTIONS = [
  { value: "partner", label: "Partner Admin", hint: "Full access — sees commissions + payouts" },
  { value: "partner_staff", label: "Technician", hint: "No commissions visibility" },
];
export default function PartnerTeam() {
  const { state } = useAuth();
  const viewer = state?.user;
  const isAdmin = viewer?.role === "partner";
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteAddCc, setInviteAddCc] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [resetForEmail, setResetForEmail] = useState(null);
  const [copiedPw, setCopiedPw] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/team");
      setMembers(data.members || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load team");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  async function invite(e) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    try {
      const { data } = await api.post("/partner/team/invite", {
        email: inviteEmail.trim(), name: inviteName.trim() || undefined,
        add_to_cc: inviteAddCc,
      });
      setTempPw(data.temp_password);
      setInviteEmail(""); setInviteName(""); setInviteAddCc(false);
      toast.success(`Login created${data.added_to_cc ? " + added to CC list" : ""} — copy the temporary password now`);
      reload();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Invite failed");
    }
  }

  async function remove(m) {
    if (!window.confirm(`Remove ${m.email} from your team? They'll lose portal access immediately.`)) return;
    try {
      await api.delete(`/partner/team/${m.id}`);
      toast.success("Removed");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
    }
  }

  async function changeRole(m, newRole) {
    if (m.role === newRole) return;
    const fromLabel = ROLE_LABEL[m.role] || m.role;
    const toLabel = ROLE_LABEL[newRole] || newRole;
    const commissionsNote = newRole === "partner"
      ? "They'll gain access to commissions & payouts."
      : "They'll lose commissions visibility.";
    if (!window.confirm(`Change ${m.email} from ${fromLabel} → ${toLabel}?\n\n${commissionsNote}`)) return;
    try {
      await api.patch(`/partner/team/${m.id}/role`, { role: newRole });
      toast.success(`Role updated to ${toLabel}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Role change failed");
    }
  }

  async function resetPassword(m) {
    if (!window.confirm(`Issue a new temporary password for ${m.email}?\n\nTheir current password will stop working immediately. You'll get the new one to share via secure channel.`)) return;
    try {
      const { data } = await api.post(`/partner/team/${m.id}/reset-password`);
      setTempPw(data.temp_password);
      setResetForEmail(data.email);
      setInviteOpen(false);
      toast.success(`Password reset for ${data.email} — copy the new temp password.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    }
  }

  async function copyPw() {
    try { await navigator.clipboard.writeText(tempPw); setCopiedPw(true); setTimeout(() => setCopiedPw(false), 2000); }
    catch { toast.error("Copy failed"); }
  }

  return (
    <div className="space-y-5" data-testid="partner-team-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight flex items-center gap-3"><Users className="w-7 h-7 text-cyan-400" /> Team</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            {isAdmin
              ? <>Invite your employees to see customer install progress. Set them as a <b className="text-slate-200">Technician</b> for onboarding-only access, or as a <b className="text-slate-200">Partner Admin</b> to also see commissions. Tick <b className="text-slate-200">Also Cc on partner emails</b> during invite if you want them looped in on every note we send.</>
              : <>Your team members. Only a Partner Admin can invite, change roles or reset passwords.</>}
          </p>
        </div>
        {isAdmin && !inviteOpen && (
          <button data-testid="team-invite-btn" onClick={() => setInviteOpen(true)}
                  className="inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <UserPlus className="w-4 h-4" /> Invite team member
          </button>
        )}
      </div>

      {isAdmin && inviteOpen && (
        <form onSubmit={invite} className="rounded-xl border border-slate-800 bg-slate-900 p-5 space-y-3" data-testid="team-invite-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Email</label>
              <input data-testid="team-invite-email" type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                     className="w-full mt-1 rounded-lg border border-slate-800 bg-slate-950 text-slate-100 px-3 py-2 outline-none focus:border-cyan-500" placeholder="employee@yourdomain.com" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Name (optional)</label>
              <input data-testid="team-invite-name" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                     className="w-full mt-1 rounded-lg border border-slate-800 bg-slate-950 text-slate-100 px-3 py-2 outline-none focus:border-cyan-500" placeholder="Matthew" />
            </div>
          </div>
          {/* v1.10.31 — opt-in CC (was auto-add for staff). Ticking
              this loops the invitee into every partner-facing outbound
              email (Kanban notes, payouts, stage automations). Leave
              unticked when they only need portal access without email
              noise from other customer installs. */}
          <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer select-none">
            <input
              data-testid="team-invite-add-cc"
              type="checkbox"
              checked={inviteAddCc}
              onChange={(e) => setInviteAddCc(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="text-slate-200 font-medium">Also Cc on partner emails</span>
              <span className="block text-[11px] text-slate-500 leading-relaxed">Loop them into every note we send this partner (Kanban activity, payout statements, stage change automations). Leave unticked to give portal access only.</span>
            </span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => { setInviteOpen(false); setInviteEmail(""); setInviteName(""); }} className="px-3 py-2 rounded-lg border border-slate-800 text-sm hover:bg-slate-950">Cancel</button>
            <button data-testid="team-invite-submit" type="submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium">
              <UserPlus className="w-4 h-4" /> Create login
            </button>
          </div>
          {tempPw && (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 mt-3" data-testid="team-invite-temppw">
              <div className="text-xs text-emerald-300 font-semibold">
                {resetForEmail
                  ? <>Password reset for <code className="font-mono">{resetForEmail}</code> — copy the new temporary password now</>
                  : "Temporary password — copy now, we won't show it again"}
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <code className="flex-1 font-mono text-sm text-emerald-200 bg-slate-950 px-3 py-2 rounded border border-slate-800 break-all">{tempPw}</code>
                <button type="button" onClick={copyPw} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-700 hover:bg-slate-800 text-slate-200 text-xs">
                  {copiedPw ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                </button>
                <button type="button" onClick={() => { setTempPw(null); setResetForEmail(null); }} className="text-xs text-slate-400 hover:text-slate-200 px-2 py-2">Done</button>
              </div>
              <div className="text-[11px] text-slate-400 mt-2">Share via secure channel. They&apos;ll be forced to change it on first login.</div>
            </div>
          )}
        </form>
      )}

      <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-950">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Added</th>
              {isAdmin && <th className="text-right px-4 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800" data-testid="team-tbody">
            {loading && <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-8 text-slate-500">Loading…</td></tr>}
            {!loading && members.length === 0 && (
              <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-10 text-slate-500">Just you so far. Invite someone above to bring them in.</td></tr>
            )}
            {members.map((m) => (
              <tr key={m.id} className={`hover:bg-slate-950/50 ${m.email === viewer?.email ? "bg-slate-950/60" : ""}`} data-testid={`team-row-${m.id}`}>
                <td className="px-4 py-3 text-slate-100">{m.name || <span className="text-slate-500">—</span>} {m.email === viewer?.email && <span className="ml-2 text-[10px] uppercase tracking-widest text-cyan-400">(you)</span>}</td>
                <td className="px-4 py-3 text-xs font-mono text-slate-300">{m.email}</td>
                <td className="px-4 py-3">
                  {m.role === "partner" ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-cyan-950 text-cyan-400 border border-cyan-900"><KeyRound className="w-3 h-3" /> Partner Admin</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">Technician</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-400">{(m.created_at || "").slice(0, 10)}</td>
                {isAdmin && (
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3 flex-wrap justify-end">
                      {m.email !== viewer?.email && (
                        <select
                          data-testid={`team-role-select-${m.id}`}
                          value={m.role}
                          onChange={(e) => changeRole(m, e.target.value)}
                          title="Change this member's role"
                          className="text-xs bg-slate-950 border border-slate-700 hover:border-cyan-500/50 focus:border-cyan-500 rounded-md px-2 py-1 text-slate-200 outline-none focus:ring-2 focus:ring-cyan-500/20 cursor-pointer"
                        >
                          {ROLE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      )}
                      {m.email !== viewer?.email && (
                        <button
                          data-testid={`team-reset-pw-${m.id}`}
                          onClick={() => resetPassword(m)}
                          title="Issue a new temporary password"
                          className="text-xs text-amber-400 hover:text-amber-300 font-medium inline-flex items-center gap-1"
                        >
                          <RotateCcw className="w-3 h-3" /> Reset password
                        </button>
                      )}
                      {m.role === "partner_staff" && m.email !== viewer?.email && (
                        <button data-testid={`team-remove-${m.id}`} onClick={() => remove(m)}
                                className="text-xs text-rose-400 hover:text-rose-300 font-medium inline-flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
